import React, { useState, useEffect } from 'react';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaLayerGroup, FaSpinner, FaArrowLeft,
  FaArrowRight, FaHospital, FaStethoscope, FaCalendarAlt, FaExclamationTriangle
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { useToast } from '../utils/toastContext';
import { formatApiError } from '../utils/apiErrorFormatter';
import {
  MATCH_BY_EXPERIENCE,
  MATCH_BY_INSTITUTION,
  MATCH_BY_OPTIONS,
  normalizeMatchBy,
  toMonthDay,
  buildCurriculumDefaultsFromRank
} from '../utils/rankUtils';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';

const MAX_ROTATIONS = 24;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// 29 Feb is selectable; it is clamped to 28 Feb in non-leap years at resolve time.
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const emptyRotation = () => ({ startDate: '', endDate: '' });

const sizeRotations = (rotations, count) => (
  Array.from({ length: count }, (_, i) => rotations?.[i] || emptyRotation())
);

const blankForm = () => ({
  name: '',
  matchBy: MATCH_BY_EXPERIENCE,
  description: '',
  defaultRotationsCount: 8,
  defaultRotations: sizeRotations([], 8)
});

/** Month + day pickers for one year-less 'MM-DD' value. */
const MonthDayPicker = ({ id, value, onChange, disabled }) => {
  const normalized = toMonthDay(value);
  const month = normalized ? parseInt(normalized.slice(0, 2), 10) : '';
  const day = normalized ? parseInt(normalized.slice(3), 10) : '';

  const emit = (nextMonth, nextDay) => {
    if (!nextMonth || !nextDay) {
      onChange('');
      return;
    }
    // Clamp the day if the new month is shorter than the previous one.
    const maxDay = DAYS_IN_MONTH[nextMonth - 1];
    const safeDay = Math.min(nextDay, maxDay);
    onChange(`${String(nextMonth).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`);
  };

  const dayCount = month ? DAYS_IN_MONTH[month - 1] : 31;

  return (
    <div className="mr-monthday">
      <select
        id={id}
        className="mr-monthday__month"
        value={month}
        disabled={disabled}
        onChange={(e) => emit(parseInt(e.target.value, 10) || 0, day || 1)}
      >
        <option value="">Month</option>
        {MONTHS.map((label, index) => (
          <option key={label} value={index + 1}>{label}</option>
        ))}
      </select>
      <select
        className="mr-monthday__day"
        value={day}
        disabled={disabled || !month}
        onChange={(e) => emit(month || 1, parseInt(e.target.value, 10) || 0)}
      >
        <option value="">Day</option>
        {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
    </div>
  );
};

const ManageRanks = () => {
  const { showSuccess, showError } = useToast();
  const [ranks, setRanks] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState('list');
  const [selectedRank, setSelectedRank] = useState(null);
  const [formData, setFormData] = useState(blankForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [replacementId, setReplacementId] = useState('');

  useEffect(() => {
    loadRanks();
    loadCurricula();
  }, []);

  const loadRanks = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/ranks/ranks');
      setRanks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(formatApiError(err, 'Failed to load ranks'));
    } finally {
      setLoading(false);
    }
  };

  // Used only to show how many curricula depend on each rank.
  const loadCurricula = async () => {
    try {
      const res = await axiosInstance.get('/curriculum/curriculums');
      setCurricula(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load curricula:', err);
    }
  };

  const getCurriculumCount = (rank) => curricula.filter(
    (c) => c?.rankId === rank?._id || (c?.rank || '').toLowerCase() === (rank?.name || '').toLowerCase()
  ).length;

  const handleCreateMode = () => {
    setSelectedRank(null);
    setFormData(blankForm());
    setMode('create');
  };

  const handleEditMode = (rank) => {
    setSelectedRank(rank);
    const count = Math.max(1, parseInt(rank?.defaultRotationsCount, 10) || 1);
    setFormData({
      name: rank?.name || '',
      matchBy: normalizeMatchBy(rank?.matchBy),
      description: rank?.description || '',
      defaultRotationsCount: count,
      defaultRotations: sizeRotations(rank?.defaultRotations, count)
    });
    setMode('edit');
  };

  const handleViewMode = (rank) => {
    setSelectedRank(rank);
    setMode('view');
  };

  const handleBackToList = () => setMode('list');

  const updateRotationCount = (nextCount) => {
    const count = Math.max(1, Math.min(MAX_ROTATIONS, parseInt(nextCount, 10) || 1));
    setFormData((prev) => ({
      ...prev,
      defaultRotationsCount: count,
      defaultRotations: sizeRotations(prev.defaultRotations, count)
    }));
  };

  const updateRotationDate = (index, key, value) => {
    setFormData((prev) => {
      const rotations = [...prev.defaultRotations];
      rotations[index] = { ...rotations[index], [key]: value };
      return { ...prev, defaultRotations: rotations };
    });
  };

  const validate = () => {
    if (!formData.name.trim()) {
      showError('Rank name is required');
      return false;
    }
    // A rotation must have both ends of its range, or neither.
    const halfFilled = formData.defaultRotations.findIndex(
      (r) => Boolean(r.startDate) !== Boolean(r.endDate)
    );
    if (halfFilled !== -1) {
      showError(`Rotation ${halfFilled + 1} needs both a start and an end date, or neither`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      name: formData.name.trim(),
      matchBy: formData.matchBy,
      description: formData.description.trim() || null,
      defaultRotationsCount: formData.defaultRotationsCount,
      defaultRotations: formData.defaultRotations
    };

    try {
      setSaving(true);
      if (mode === 'edit' && selectedRank?._id) {
        await axiosInstance.put(`/ranks/update_rank/${selectedRank._id}`, payload);
        showSuccess('Rank updated');
      } else {
        await axiosInstance.post('/ranks/create_rank', payload);
        showSuccess('Rank created');
      }
      await loadRanks();
      setMode('list');
    } catch (err) {
      showError(formatApiError(err, 'Failed to save rank'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rank, replacement = null) => {
    if (!rank?._id) return;
    try {
      const res = await axiosInstance.delete(`/ranks/rank/${rank._id}`, {
        params: replacement ? { replacement } : undefined
      });

      // The rank is still referenced — the API asks for a replacement first.
      if (res.data?.requires_replacement) {
        setDeleteTarget({ rank, warning: res.data.warning, usage: res.data.usage });
        setReplacementId('');
        return;
      }

      showSuccess('Rank deleted');
      setDeleteTarget(null);
      setReplacementId('');
      await Promise.all([loadRanks(), loadCurricula()]);
      if (selectedRank?._id === rank._id) setSelectedRank(null);
      setMode('list');
    } catch (err) {
      showError(formatApiError(err, 'Failed to delete rank'));
    }
  };

  const filteredRanks = ranks.filter((rank) => {
    const haystack = `${rank?.name || ''} ${rank?.matchBy || ''} ${rank?.description || ''}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  const experienceCount = ranks.filter((r) => normalizeMatchBy(r.matchBy) === MATCH_BY_EXPERIENCE).length;
  const institutionCount = ranks.filter((r) => normalizeMatchBy(r.matchBy) === MATCH_BY_INSTITUTION).length;

  const stepsByMode = {
    list: ['list'],
    view: ['list', 'view'],
    create: ['list', 'create'],
    edit: ['list', 'edit']
  };
  const labelsByStep = {
    list: 'Ranks',
    view: 'Manage Rank',
    create: 'Create Rank',
    edit: 'Edit Rank'
  };
  const activeSteps = stepsByMode[mode] || ['list'];
  const stepCanGo = (step) => activeSteps.includes(step);
  const goToStep = (step) => {
    if (!stepCanGo(step)) return;
    if (step === 'list') { handleBackToList(); return; }
    if (step === 'view' && selectedRank) { setMode('view'); return; }
    if (step === 'edit' && selectedRank) { handleEditMode(selectedRank); return; }
    if (step === 'create') { handleCreateMode(); }
  };

  const matchByIcon = (matchBy) => (
    normalizeMatchBy(matchBy) === MATCH_BY_INSTITUTION ? <FaHospital /> : <FaStethoscope />
  );

  const matchByLabel = (matchBy) => (
    MATCH_BY_OPTIONS.find((o) => o.value === normalizeMatchBy(matchBy))?.label || 'Experience'
  );

  if (loading) {
    return (
      <div className="mc-page" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <FaSpinner className="mc-spin" style={{ fontSize: '3rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  // Live preview of the concrete dates this rank's defaults resolve to today.
  const previewDefaults = buildCurriculumDefaultsFromRank({
    defaultRotationsCount: formData.defaultRotationsCount,
    defaultRotations: formData.defaultRotations
  });

  return (
    <div className="mc-page">
      <div className="universal-white-container mc-shell">
        <div className="mc-steps-bar">
          {['list', ...(mode === 'create' ? ['create'] : mode === 'edit' ? ['edit'] : ['view', 'edit'].filter(stepCanGo))].map((step, index, arr) => (
            <React.Fragment key={step}>
              <button
                type="button"
                className={[
                  'mc-step',
                  mode === step ? 'is-active' : '',
                  stepCanGo(step) ? 'is-enabled' : 'is-disabled'
                ].join(' ')}
                onClick={() => goToStep(step)}
              >
                {labelsByStep[step]}
              </button>
              {index < arr.length - 1 && <FaArrowRight className="mc-step-arrow" />}
            </React.Fragment>
          ))}
        </div>

        {(mode === 'list' || mode === 'view') && (
          <div>
            <div className="mc-hero">
              <div className="mc-hero__copy">
                <span className="mc-eyebrow">Admin</span>
                <h1>Manage Ranks</h1>
                <p>Define the ranks curricula are built on, how they match preceptors, and their rotation defaults.</p>
              </div>
              <div className="mc-hero__stats">
                <div className="mc-stat">
                  <strong>{ranks.length}</strong>
                  <span>Ranks</span>
                </div>
                <div className="mc-stat">
                  <strong>{experienceCount}</strong>
                  <span>By Experience</span>
                </div>
                <div className="mc-stat">
                  <strong>{institutionCount}</strong>
                  <span>By Institution</span>
                </div>
                <div className="mc-stat">
                  <strong>{curricula.length}</strong>
                  <span>Curricula</span>
                </div>
              </div>
            </div>

            <div className="mc-toolbar">
              <div className="mc-search-box">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search by name or match type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="mc-primary-btn" type="button" onClick={handleCreateMode}>
                <FaPlus /> New Rank
              </button>
            </div>

            <div className="mc-cards-grid">
              {filteredRanks.length === 0 ? (
                <div className="mc-empty-state">
                  <FaLayerGroup />
                  <h3>{ranks.length === 0 ? 'No ranks yet' : 'No matching ranks'}</h3>
                  <p>
                    {ranks.length === 0
                      ? 'Create your first rank — curricula need one before they can be created'
                      : 'Try a different search term'}
                  </p>
                </div>
              ) : (
                filteredRanks.map((rank) => {
                  const usedBy = getCurriculumCount(rank);
                  return (
                    <div
                      key={rank._id}
                      className={[
                        'mc-curriculum-card',
                        `mr-card--${normalizeMatchBy(rank.matchBy)}`,
                        selectedRank?._id === rank._id && mode === 'view' ? 'is-active' : ''
                      ].join(' ')}
                      onClick={() => handleViewMode(rank)}
                    >
                      <div className="mc-curriculum-card__top">
                        <div>
                          <h4>{rank.name}</h4>
                          <p className="mc-curriculum-card__rank">
                            {matchByIcon(rank.matchBy)} Matched by {matchByLabel(rank.matchBy)}
                          </p>
                        </div>
                        <div className={`mc-status-pill mr-pill--${normalizeMatchBy(rank.matchBy)}`}>
                          {matchByLabel(rank.matchBy)}
                        </div>
                      </div>

                      {rank.description && (
                        <div className="mc-curriculum-card__meta">
                          <span>{rank.description}</span>
                        </div>
                      )}

                      <div className="mc-card-metrics">
                        <div className="mc-card-metric">
                          <strong>{rank.defaultRotationsCount ?? '—'}</strong>
                          <small>Rotations</small>
                        </div>
                        <div className="mc-card-metric">
                          <strong>{(rank.defaultRotations || []).filter((r) => r.startDate && r.endDate).length}</strong>
                          <small>Dated</small>
                        </div>
                        <div className="mc-card-metric">
                          <strong>{usedBy}</strong>
                          <small>Curricula</small>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                        <button className="mc-secondary-btn" type="button" onClick={(e) => { e.stopPropagation(); handleEditMode(rank); }}>
                          <FaEdit /> Edit
                        </button>
                        <button className="mc-danger-btn" type="button" onClick={(e) => { e.stopPropagation(); handleDelete(rank); }}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {mode === 'view' && selectedRank && (
              <div className="mc-editor-panel mc-transition-up" style={{ marginTop: '1.25rem' }}>
                <div className="mc-editor-head">
                  <div>
                    <h3>🎓 {selectedRank.name}</h3>
                    <p>Rotation defaults applied when a curriculum picks this rank</p>
                  </div>
                  <div className="mc-editor-actions">
                    <button className="mc-secondary-btn is-on-dark" type="button" onClick={handleBackToList}>
                      <FaArrowLeft /> Back
                    </button>
                    <button className="mc-primary-btn" type="button" onClick={() => handleEditMode(selectedRank)}>
                      <FaEdit /> Manage / Edit
                    </button>
                    <button className="mc-danger-btn" type="button" onClick={() => handleDelete(selectedRank)}>
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>

                <div className="mc-grid-2">
                  <div className="mc-form-card">
                    <h4 className="mc-subcard-title">Details</h4>
                    <div className="mc-overview-list">
                      <div>
                        <strong>Matching</strong>
                        <span>By {matchByLabel(selectedRank.matchBy).toLowerCase()}</span>
                      </div>
                      <div>
                        <strong>Default rotations</strong>
                        <span>{selectedRank.defaultRotationsCount ?? '—'}</span>
                      </div>
                      <div>
                        <strong>Used by</strong>
                        <span>{getCurriculumCount(selectedRank)} curricula</span>
                      </div>
                    </div>
                  </div>
                  <div className="mc-form-card">
                    <h4 className="mc-subcard-title">Description</h4>
                    <p style={{ margin: 0, color: '#475569' }}>
                      {selectedRank.description || 'No description provided.'}
                    </p>
                  </div>
                </div>

                <div className="mc-form-card" style={{ marginTop: '1rem' }}>
                  <h4 className="mc-subcard-title">
                    <FaCalendarAlt /> Default rotation dates
                  </h4>
                  <p className="mr-hint">
                    Stored without a year. When a curriculum loads them, each date resolves to its
                    closest upcoming occurrence — shown below as of today.
                  </p>
                  <RotationPreviewTable
                    rotations={sizeRotations(selectedRank.defaultRotations, selectedRank.defaultRotationsCount || 0)}
                    resolved={selectedRank.resolvedDefaultRotations}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {(mode === 'create' || mode === 'edit') && (
          <div className="mc-editor-panel mc-transition-up">
            <div className="mc-editor-head">
              <div>
                <h3>{mode === 'edit' ? `✏️ Edit ${selectedRank?.name}` : '➕ Create Rank'}</h3>
                <p>Ranks drive curriculum matching and rotation defaults</p>
              </div>
              <div className="mc-editor-actions">
                <button className="mc-secondary-btn is-on-dark" type="button" onClick={handleBackToList}>
                  <FaArrowLeft /> Cancel
                </button>
                <button className="mc-primary-btn" type="button" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Rank'}
                </button>
              </div>
            </div>

            <div className="mc-grid-2">
              <div className="mc-form-card">
                <h4 className="mc-subcard-title">Basic info</h4>
                <label className="mc-field">
                  Rank name
                  <input
                    type="text"
                    value={formData.name}
                    placeholder="e.g. PharmD"
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </label>
                <label className="mc-field" style={{ marginTop: '0.85rem' }}>
                  Description <span style={{ fontWeight: 400, color: '#64748b' }}>(optional)</span>
                  <input
                    type="text"
                    value={formData.description}
                    placeholder="Short note about this rank"
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </label>
              </div>

              <div className="mc-form-card">
                <h4 className="mc-subcard-title">Matching</h4>
                <p className="mr-hint">
                  Determines what a curriculum of this rank matches preceptors on — and therefore
                  how preceptor availability is recorded.
                </p>
                <div className="mr-matchby">
                  {MATCH_BY_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`mr-matchby__option ${formData.matchBy === option.value ? 'is-active' : ''}`}
                      onClick={() => setFormData({ ...formData, matchBy: option.value })}
                    >
                      <span className="mr-matchby__icon">{matchByIcon(option.value)}</span>
                      <span className="mr-matchby__label">{option.label}</span>
                      <span className="mr-matchby__desc">{option.description}</span>
                    </button>
                  ))}
                </div>
                {mode === 'edit' && normalizeMatchBy(selectedRank?.matchBy) !== formData.matchBy && (
                  <div className="mr-warning">
                    <FaExclamationTriangle />
                    <span>
                      Changing the match type clears existing constraints on curricula using this
                      rank, because their entities are no longer the right type.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="mc-form-card" style={{ marginTop: '1rem' }}>
              <div className="mc-section-title-row">
                <h4 className="mc-subcard-title">
                  <FaCalendarAlt /> Default rotations
                </h4>
                <div className="mr-counter">
                  <button
                    type="button"
                    className="mc-ghost-btn"
                    onClick={() => updateRotationCount(formData.defaultRotationsCount - 1)}
                    disabled={formData.defaultRotationsCount <= 1}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={MAX_ROTATIONS}
                    value={formData.defaultRotationsCount}
                    onChange={(e) => updateRotationCount(e.target.value)}
                  />
                  <button
                    type="button"
                    className="mc-ghost-btn"
                    onClick={() => updateRotationCount(formData.defaultRotationsCount + 1)}
                    disabled={formData.defaultRotationsCount >= MAX_ROTATIONS}
                  >
                    +
                  </button>
                </div>
              </div>

              <p className="mr-hint">
                Dates are stored without a year. When a curriculum picks this rank, each date
                resolves to its closest upcoming occurrence, rolling into the next year once it has
                passed. Leave a rotation blank to have the curriculum form fill it in instead.
              </p>

              <div className="mr-rotations">
                {formData.defaultRotations.map((rotation, index) => {
                  const preview = previewDefaults.rotations[index];
                  return (
                    <div key={index} className="mr-rotation">
                      <div className="mr-rotation__head">
                        <span className="mr-rotation__badge">{index + 1}</span>
                        <span className="mr-rotation__title">Rotation {index + 1}</span>
                      </div>
                      <div className="mr-rotation__fields">
                        <label className="mc-field">
                          Starts
                          <MonthDayPicker
                            id={`rank-rot-${index}-start`}
                            value={rotation.startDate}
                            onChange={(v) => updateRotationDate(index, 'startDate', v)}
                          />
                        </label>
                        <label className="mc-field">
                          Ends
                          <MonthDayPicker
                            id={`rank-rot-${index}-end`}
                            value={rotation.endDate}
                            onChange={(v) => updateRotationDate(index, 'endDate', v)}
                          />
                        </label>
                      </div>
                      {preview?.startDate && preview?.endDate && (
                        <div className="mr-rotation__preview">
                          Resolves to {new Date(preview.startDate).toLocaleDateString()} → {new Date(preview.endDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {deleteTarget && (
          <div className="mr-modal-backdrop" onClick={() => setDeleteTarget(null)}>
            <div className="mr-modal" onClick={(e) => e.stopPropagation()}>
              <h3><FaExclamationTriangle /> Rank still in use</h3>
              <p>{deleteTarget.warning}</p>
              <label className="mc-field">
                Reassign to
                <select value={replacementId} onChange={(e) => setReplacementId(e.target.value)}>
                  <option value="">Select a replacement rank…</option>
                  {ranks
                    .filter((r) => r._id !== deleteTarget.rank._id)
                    .map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.name} — by {r.matchBy}
                      </option>
                    ))}
                </select>
              </label>
              <div className="mr-modal__actions">
                <button className="mc-secondary-btn" type="button" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </button>
                <button
                  className="mc-danger-btn"
                  type="button"
                  disabled={!replacementId}
                  onClick={() => handleDelete(deleteTarget.rank, replacementId)}
                >
                  <FaTrash /> Reassign & Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
      </div>
    </div>
  );
};

/** Read-only table of a rank's stored MM-DD ranges next to their resolved dates. */
const RotationPreviewTable = ({ rotations, resolved }) => {
  if (!rotations.length) {
    return <p style={{ color: '#64748b', margin: 0 }}>No rotations configured.</p>;
  }

  return (
    <div className="mr-preview-table-wrap">
      <table className="mr-preview-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Stored range</th>
            <th>Resolves to</th>
          </tr>
        </thead>
        <tbody>
          {rotations.map((rotation, index) => {
            const concrete = resolved?.[index];
            const hasStored = rotation.startDate && rotation.endDate;
            return (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>
                  {hasStored
                    ? `${rotation.startDate} → ${rotation.endDate}`
                    : <span style={{ color: '#94a3b8' }}>Not set</span>}
                </td>
                <td>
                  {concrete?.startDate && concrete?.endDate
                    ? `${new Date(concrete.startDate).toLocaleDateString()} → ${new Date(concrete.endDate).toLocaleDateString()}`
                    : <span style={{ color: '#94a3b8' }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ManageRanks;
